import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

const useNoLineContext = () => null;

const ReviewTableRow = ({
  line,
  lineIndex,
  columns,
  renderLineActions,
  useLineContext
}) => {
  const useRowContext = useLineContext || useNoLineContext;
  const lineContext = useRowContext(line);

  return (
    <TableRow>
      {columns.map(({ id, render }) => (
        <TableCell key={id}>{render(line, lineIndex, lineContext)}</TableCell>
      ))}
      {renderLineActions && (
        <TableCell>{renderLineActions(line, lineIndex, lineContext)}</TableCell>
      )}
    </TableRow>
  );
};

const ReviewLinesTable = ({
  columns,
  lines,
  getLineKey,
  renderLineActions,
  actionsLabel,
  useLineContext
}) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map(({ id, label }) => (
            <TableCell key={id}>{label}</TableCell>
          ))}
          {renderLineActions && <TableCell>{actionsLabel}</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line, lineIndex) => (
          <ReviewTableRow
            key={getLineKey(line, lineIndex)}
            line={line}
            lineIndex={lineIndex}
            columns={columns}
            renderLineActions={renderLineActions}
            useLineContext={useLineContext}
          />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// One source file of a review batch: an accordion whose header carries the
// file's identity and flag chips, and whose body is a column-configured line
// table. Shared by BOM bulk upload and Smart Upload review (ticket #378 --
// Design Intent 3.3, one review table for every document family).
//
// `columns` is [{ id, label, render(line, lineIndex, lineContext) }].
// `useLineContext`, when given, is a hook called once per rendered line; its
// result reaches every column renderer and the actions slot, so a consumer can
// hold per-line state (drafts, debounced saves) without the shell knowing it.
// `body`, when not undefined, replaces the line table (read-only rows, a
// low-confidence parse, a file still being read).
const ReviewTableShell = ({
  title,
  subtitle,
  chips,
  banners,
  needsAttention,
  defaultExpanded = false,
  columns = [],
  lines = [],
  getLineKey = (line, lineIndex) => lineIndex,
  renderLineActions,
  actionsLabel = "",
  useLineContext,
  body
}) => {
  const { palette } = useTheme();

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        border: `1px solid ${
          needsAttention ? palette.error.light : palette.backgroundGray.dark
        }`,
        "&:before": { display: "none" }
      }}
    >
      <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexWrap: "wrap",
            gap: 1,
            pr: 1
          }}
        >
          <Box>
            <Typography variant="subtitle1">{title}</Typography>
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {chips}
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {banners}
        {body !== undefined ? (
          body
        ) : (
          <ReviewLinesTable
            columns={columns}
            lines={lines}
            getLineKey={getLineKey}
            renderLineActions={renderLineActions}
            actionsLabel={actionsLabel}
            useLineContext={useLineContext}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ReviewTableShell;
